import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Projects.module.css';
import { projectsConfig, neighborhoodProjects } from './projectsConfig';
import { loadThumbnail, loadProjectGallery } from './imageLoader';
import LazyImage from './LazyImage';

const MoreProjects = ({ onOpenLead }) => {
  const sectionRef = useRef(null);
  const modalRef = useRef(null);
  const thumbnailRefs = useRef([]);

  const [neighborhoods, setNeighborhoods] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectGallery, setProjectGallery] = useState([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visibleThumbnails, setVisibleThumbnails] = useState(new Set());

  const handleLeadFromProject = () => {
    closeProject();
    if (typeof onOpenLead === 'function') {
      setTimeout(onOpenLead, 120);
    }
  };

  useEffect(() => {
    const load = async (cfgs) => Promise.all(cfgs.map(async (c) => {
      try {
        const t = await loadThumbnail(c.folderName, c.thumbnailIndex);
        const heroIdx = c.heroIndex || c.thumbnailIndex;
        const hero = heroIdx === c.thumbnailIndex ? t : await loadThumbnail(c.folderName, heroIdx).catch(() => t);
        return { ...c, thumbnail: t, mainImage: hero, gallery: [] };
      }
      catch { return { ...c, thumbnail: null, mainImage: null, gallery: [] }; }
    }));
    load(neighborhoodProjects).then(setNeighborhoods);
    load(projectsConfig).then(setProjects);
  }, []);

  useEffect(() => {
    if (!projects.length && !neighborhoods.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { const i = parseInt(e.target.dataset.index); setTimeout(() => setVisibleThumbnails(p => new Set([...p, i])), i * 60); } });
    }, { threshold: 0.15, rootMargin: '0px 0px -30px 0px' });
    thumbnailRefs.current.forEach((r) => { if (r) obs.observe(r); });
    return () => obs.disconnect();
  }, [projects, neighborhoods]);

  const openProject = async (project) => {
    setSelectedProject(project);
    setIsLoadingGallery(true);
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed'; document.body.style.top = `-${scrollY}px`; document.body.style.width = '100%';
    document.body.setAttribute('data-scroll-position', scrollY.toString());
    const imgs = await loadProjectGallery(project.folderName, project.imageCount);
    setProjectGallery(imgs); setIsLoadingGallery(false);
  };

  const closeProject = () => {
    setSelectedProject(null); setProjectGallery([]); setIsGalleryOpen(false);
    const pos = document.body.getAttribute('data-scroll-position');
    document.body.style.position = ''; document.body.style.top = ''; document.body.style.width = '';
    document.body.removeAttribute('data-scroll-position');
    if (pos) window.scrollTo(0, parseInt(pos, 10));
  };

  useEffect(() => { if (selectedProject && modalRef.current) modalRef.current.scrollTop = 0; }, [selectedProject]);

  const openGallery = (i) => { setCurrentImageIndex(i); setIsGalleryOpen(true); };
  const closeGallery = () => setIsGalleryOpen(false);
  const nextImage = () => setCurrentImageIndex(p => (p + 1) % projectGallery.length);
  const prevImage = () => setCurrentImageIndex(p => (p - 1 + projectGallery.length) % projectGallery.length);

  useEffect(() => {
    const h = (e) => {
      if (isGalleryOpen) { if (e.key === 'ArrowRight') prevImage(); if (e.key === 'ArrowLeft') nextImage(); if (e.key === 'Escape') closeGallery(); }
      else if (selectedProject && e.key === 'Escape') closeProject();
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [isGalleryOpen, selectedProject, projectGallery]);

  useEffect(() => { return () => { document.body.style.overflow = ''; }; }, []);

  let idx = 0;

  return (
    <section className={styles.projectsSection} ref={sectionRef} id="projects">
      <div className={styles.bgDecoration}><div className={styles.gridPattern} /><div className={styles.cornerAccent} /><div className={styles.cornerAccentBottom} /></div>

      <header className={styles.titleSection}>
        <h2 className={styles.sectionTitle}>הפרויקטים שלנו</h2>
        <p className={styles.sectionSubtitle}>כל פרויקט - עולם שלם</p>
      </header>

      {/* Regular projects - 4 col grid like narkis */}
      <div className={styles.projectsGrid}>
        {projects.map((p) => {
          const i = idx++;
          return (
            <div key={p.id} ref={el => thumbnailRefs.current[i] = el} data-index={i}
              className={`${styles.card} ${visibleThumbnails.has(i) ? styles.visible : ''}`}
              style={{ '--index': i }} onClick={() => openProject(p)}>
              <div className={styles.thumbWrap}>{p.thumbnail && <LazyImage src={p.thumbnail} alt={p.title} className={styles.thumbImg} rootMargin="200px" />}<div className={styles.thumbShine} /></div>
              <div className={styles.thumbContent}><h3 className={styles.thumbTitle}>{p.title}</h3><p className={styles.thumbSub}>{p.subtitle}</p><div className={styles.thumbMeta}><span>{p.imageCount} תמונות</span></div></div>
              <div className={styles.thumbOverlay}><div className={styles.overlayInner}><span className={styles.viewText}>צפו בפרויקט</span><div className={styles.viewArrow}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg></div></div></div>
              {p.featured && <div className={styles.featRibbon}><span className={styles.featLabel}>פרויקט מרכזי</span><div className={styles.featName}>הנחלה של זוהר</div></div>}
            </div>
          );
        })}
      </div>

      {/* Neighborhoods - flagship, at the end */}
      {neighborhoods.length > 0 && (
        <>
          <div className={styles.divider}><span className={styles.divLine} /><h3 className={styles.divTitle}>תכנון שכונות מגורים</h3><span className={styles.divLine} /></div>
          <div className={styles.nhHeader}>
            <p className={styles.nhSub}>פרויקטי דגל - תכנון שכונות שלמות מאפס</p>
          </div>
          <div className={styles.nhGrid}>
            {neighborhoods.map((p) => {
              const i = idx++;
              return (
                <div key={p.id} ref={el => thumbnailRefs.current[i] = el} data-index={i}
                  className={`${styles.nhCard} ${visibleThumbnails.has(i) ? styles.visible : ''}`}
                  style={{ '--index': i }} onClick={() => openProject(p)}>
                  <div className={styles.thumbWrap}>{p.thumbnail && <LazyImage src={p.thumbnail} alt={p.title} className={styles.thumbImg} rootMargin="200px" />}<div className={styles.thumbShine} /></div>
                  <div className={styles.nhRibbon}><span>{p.ribbon}</span></div>
                  <div className={styles.thumbContent}><h3 className={styles.thumbTitle}>{p.title}</h3><p className={styles.thumbSub}>{p.subtitle}</p><div className={styles.thumbMeta}><span>{p.imageCount} תמונות</span></div></div>
                  <div className={styles.thumbOverlay}><div className={styles.overlayInner}><span className={styles.viewText}>צפו בפרויקט</span><div className={styles.viewArrow}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg></div></div></div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className={styles.ctaSection}><button type="button" onClick={onOpenLead} className={styles.ctaButton}><span className={styles.ctaText}>לשיחת היכרות לחצו כאן</span><div className={styles.ctaArrow}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg></div></button></div>

      {/* Modal */}
      {selectedProject && createPortal((
        <div className={styles.modalBackdrop} ref={modalRef}>
          <div className={styles.modal}>
            <button className={styles.closeBtn} onClick={closeProject}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
            <div className={styles.modalHero}>{selectedProject.mainImage && <img src={selectedProject.mainImage} alt={selectedProject.title} className={styles.heroImage} />}<div className={styles.heroOverlay} /></div>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}><div className={styles.modalMeta}><span>{selectedProject.imageCount} תמונות</span></div><h2 className={styles.modalTitle}>{selectedProject.title}</h2><p className={styles.modalSubtitle}>{selectedProject.subtitle}</p>{selectedProject.description && <p className={styles.modalDesc}>{selectedProject.description}</p>}{selectedProject.credit && <p className={styles.modalCredit}>צילום: {selectedProject.credit}</p>}</div>
              <div className={styles.gallerySection}>
                <h4 className={styles.gallerySectionTitle}><span className={styles.galleryTitleLine} />גלריית תמונות<span className={styles.galleryTitleLine} /></h4>
                {isLoadingGallery ? <div className={styles.galleryLoader}><div className={styles.spinner} /><p>טוען תמונות...</p></div> : (
                  <div className={styles.modalGallery}>{projectGallery.map((img, i) => (
                    <div key={i} className={styles.galleryThumb} onClick={() => openGallery(i)}><LazyImage src={img} alt={`${selectedProject.title} ${i+1}`} rootMargin="300px" /><div className={styles.galleryThumbOv}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/></svg></div></div>
                  ))}</div>
                )}
                <div className={styles.modalCtaSection}><button type="button" onClick={handleLeadFromProject} className={styles.modalCtaBtn}><span>אהבתם? בואו נדבר</span></button></div>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Lightbox */}
      {isGalleryOpen && projectGallery.length > 0 && createPortal((
        <div className={styles.lightbox}>
          <button className={styles.lbClose} onClick={closeGallery}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
          <div className={styles.lbViewer}><img src={projectGallery[currentImageIndex]} alt="" className={styles.lbImg} /></div>
          <button className={`${styles.lbNav} ${styles.lbPrev}`} onClick={prevImage}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg></button>
          <button className={`${styles.lbNav} ${styles.lbNext}`} onClick={nextImage}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg></button>
          <div className={styles.lbCounter}>{currentImageIndex + 1} / {projectGallery.length}</div>
          <div className={styles.lbThumbs}>{projectGallery.map((img, i) => (
            <div key={i} className={`${styles.lbThumb} ${i === currentImageIndex ? styles.lbThumbOn : ''}`} onClick={() => setCurrentImageIndex(i)}><img src={img} alt="" /></div>
          ))}</div>
        </div>
      ), document.body)}
    </section>
  );
};

export default MoreProjects;
